import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  History as HistoryIcon,
  Joystick,
  Pause,
  Play,
  Rewind,
  FastForward,
  SkipBack,
  SkipForward,
  Square,
  Volume1,
  Volume2,
} from "lucide-react";
import clsx from "clsx";
import { Api, type LibraryItem } from "../lib/api.js";
import { getActiveProfileId } from "../lib/profile.js";

/**
 * Phone-as-remote (2026-10-28). Born from the G20S whose BLE radio died
 * after pairing — until a replacement remote lands, the phone IS the
 * remote. Pure frontend: /api/cast/mpv/command already proxies transport
 * controls to the chosen Pi's mpv through its reverse tunnel.
 *
 * The resume pulldown lists the last 5 things this profile started but
 * never finished. Picking one just queues a normal cast play — the TV's
 * watch page resumes from the stored position on its own (Watch.tsx:
 * resume.position_s > 10 wins), so "start at the last known place" needs
 * no position plumbing through cast at all.
 *
 * Layout is thumb-first: big targets, no scrolling, works one-handed.
 */

type HistoryEntry = LibraryItem & { last_position_s: number; last_seen_at: string; last_event: string };

export function Remote(): JSX.Element {
  const devicesQ = useQuery({
    queryKey: ["cast-devices"],
    queryFn: Api.cast.devices,
    refetchInterval: 15_000,
    retry: false,
  });
  const pis = devicesQ.data?.devices ?? [];
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const deviceId = picked ?? pis[0]?.device_id;
  const selectedLabel = pis.find((p) => p.device_id === deviceId)?.label ?? null;

  const nowQ = useQuery({
    queryKey: ["cast-now-playing"],
    queryFn: Api.cast.nowPlaying,
    refetchInterval: 5_000,
    retry: false,
  });
  const nowPlaying = nowQ.data?.active ? (nowQ.data.title ?? "Playing") : null;

  // Live mpv position — drives the +15 min button's end-of-video guard.
  const statusQ = useQuery({
    queryKey: ["cast-mpv-status", deviceId],
    queryFn: () => Api.cast.mpvStatus(deviceId),
    refetchInterval: 5_000,
    retry: false,
  });
  const st = statusQ.data;
  const remainingS =
    st?.active && typeof st.position === "number" && typeof st.duration === "number"
      ? st.duration - st.position
      : null;

  // Resume candidates: watched but not completed, newest first (history is
  // already last_seen_at-desc). >10 s matches the watch page's own "worth
  // resuming" threshold — anything under that effectively starts over.
  const profileId = getActiveProfileId();
  const historyQ = useQuery({
    queryKey: ["history", profileId],
    queryFn: () => Api.history(profileId!),
    enabled: !!profileId,
    staleTime: 30_000,
  });
  const resumable = (historyQ.data?.items ?? [])
    .filter((it) => it.last_event !== "completed" && it.last_position_s > 10)
    .slice(0, 5);

  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function send(action: string, value?: number): Promise<void> {
    if (busy) return;
    setBusy(action);
    setErr(null);
    setNote(null);
    try {
      const r = await Api.cast.mpvCommand(deviceId, action, value);
      if (!r.ok) throw new Error(r.error ?? "command failed");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function resume(item: HistoryEntry): Promise<void> {
    if (busy || !profileId) return;
    setBusy("resume");
    setErr(null);
    setNote(null);
    try {
      const r = await Api.cast.play({
        item_id: item.id,
        kind: item.kind,
        profile_id: profileId,
        title: item.title,
        ...(deviceId !== undefined && { device_id: deviceId }),
      });
      if (!r.ok) throw new Error(r.error ?? "play failed");
      const pct = item.duration_s ? Math.round((item.last_position_s / item.duration_s) * 100) : null;
      setNote(
        `Starting “${item.title}”${r.label ? ` on ${r.label}` : ""} — resumes ${pct !== null ? `${pct}% in` : "where you left off"}.`,
      );
      void nowQ.refetch();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const Btn = ({
    action,
    value,
    label,
    Icon,
    big = false,
    danger = false,
    off = false,
  }: {
    action: string;
    value?: number;
    label: string;
    Icon: typeof Play;
    big?: boolean;
    danger?: boolean;
    off?: boolean;
  }): JSX.Element => (
    <button
      onClick={() => void send(action, value)}
      disabled={!!busy || !deviceId || off}
      className={clsx(
        "flex flex-col items-center justify-center gap-1 rounded-xl border transition active:scale-95 disabled:opacity-40",
        big ? "h-28 text-lg" : "h-20 text-sm",
        danger
          ? "bg-vermilion/15 border-vermilion/40 text-vermilionHi"
          : "bg-ink/60 border-hairline text-ivory hover:border-gold",
      )}
    >
      <Icon size={big ? 34 : 22} className={busy === action ? "animate-pulse" : undefined} />
      {label}
    </button>
  );

  return (
    <div className="max-w-md mx-auto">
      <h1 className="font-display text-3xl text-ivory flex items-center gap-3">
        <Joystick size={24} className="text-gold" /> Remote
        {selectedLabel && <span className="text-base text-parchment/70">· {selectedLabel}</span>}
      </h1>
      <div className="ornament-rule" />

      {pis.length > 1 && (
        <select
          className="w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none mb-4"
          value={deviceId ?? ""}
          onChange={(e) => setPicked(e.target.value || undefined)}
        >
          {pis.map((p) => (
            <option key={p.device_id} value={p.device_id}>
              {p.label} — {p.online ? "online" : "offline"}
            </option>
          ))}
        </select>
      )}

      {nowPlaying && (
        <div className="card p-3 mb-4 text-sm text-parchment flex items-center gap-2">
          <span className="block w-1.5 h-1.5 rounded-full bg-vermilion animate-pulse flex-shrink-0" />
          <span className="truncate">{nowPlaying}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Btn action="seek_back" value={10} label="−10 s" Icon={SkipBack} />
        <Btn action="pause" label="Play / Pause" Icon={nowPlaying ? Pause : Play} big />
        <Btn action="seek_fwd" value={30} label="+30 s" Icon={SkipForward} />
        <Btn action="seek_back" value={900} label="−15 min" Icon={Rewind} />
        <Btn action="stop" label="Stop" Icon={Square} danger />
        <Btn action="seek_fwd" value={900} label="+15 min" Icon={FastForward} off={remainingS !== null && remainingS < 900} />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Btn action="volume_down" value={5} label="Vol −" Icon={Volume1} />
        <Btn action="volume_up" value={5} label="Vol +" Icon={Volume2} />
      </div>

      {profileId && resumable.length > 0 && (
        <section className="mt-6">
          <div className="section-label flex items-center gap-2 mb-2">
            <HistoryIcon size={13} /> Pick up where you left off
          </div>
          <select
            className="w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
            value=""
            disabled={!!busy}
            onChange={(e) => {
              const it = resumable.find((r) => r.id === e.target.value);
              if (it) void resume(it);
            }}
          >
            <option value="" disabled>
              — last {resumable.length} unfinished {resumable.length === 1 ? "item" : "items"} —
            </option>
            {resumable.map((it) => (
              <option key={it.id} value={it.id}>
                {it.title}
                {it.duration_s ? ` — ${Math.round((it.last_position_s / it.duration_s) * 100)}% in` : ""}
              </option>
            ))}
          </select>
        </section>
      )}

      {note && (
        <div className="mt-4 rounded-md border border-jade/50 bg-jade/15 px-3 py-2.5 text-sm text-emerald-300">
          ✓ {note}
        </div>
      )}
      {err && (
        <div className="mt-4 rounded-md border border-vermilion/50 bg-vermilion/15 px-3 py-2.5 text-sm text-vermilionHi">
          ✗ {err}
        </div>
      )}

      <p className="mt-6 text-xs text-parchment/50 leading-relaxed text-center">
        Works from anywhere — commands route through the cloud to the Pi.
      </p>
    </div>
  );
}
